import { Page } from "components/Page";
import Button from "components/shared/button";
import { MyEtudesTable } from "components/tables/MyEtudesTable";
import React, { useCallback, useEffect, useRef, useState } from "react";
import useTranslation from "utils/hooks/useTranslation";
import { useGetEtudesListQuery } from "store/api";
import { useSalesNetwork } from "utils/hooks/useSalesNetworks";
import Loader from "components/shared/loader";
import { DeletePopup } from "components/popups/DeletePopup";
import { usePopup } from "providers/PopupProvider";
import { ServiceSelectItem } from "components/ServiceSelectItem";
import { MailPopup } from "components/popups/MailPopup";
import { EtudeStep } from "utils/defines";
import { PdfDownloadPopup } from "components/popups/PdfDownloadPopup";

export const MyEtudesPage: React.FC = () => {
  const { t, language } = useTranslation();
  const { openPopup, closeLastPopup } = usePopup();
  const title = t("FO_STUDIES_MY_STUDIES");
  const searchPlaceholder = t("FO_STUDIES_SEARCH");
  const { network, isLoading: isSalesNetworkLoading } = useSalesNetwork();

  const [selectedRowIDs, setSelectedRowIDs] = useState<number[]>([]);
  const currentSelectedRowSteps = useRef<number[]>([]);
  const [selectedRowsCount, setSelectedRowsCount] = useState<number>(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [inputSearchValue, setInputSearchValue] = useState<string>("");
  const [etudesListData, setEtudesListData] = useState<any[]>([]);

  const [isEtudeListDataLoading, setIsEtudeListDataLoading] = useState(false);
  const [isPdfURLLoading, setIsPdfURLLoading] = useState<boolean>(false);

  const { data: etudesData, refetch: refetchEtudeListData } =
    useGetEtudesListQuery({
      code: network?.code,
      pageSize: 30,
      page: currentPage,
      searchQuery: searchQuery,
    });

  // Normalize etudesData to array
  useEffect(() => {
    if (etudesData?.etudes) {
      if (Array.isArray(etudesData.etudes)) {
        setEtudesListData(etudesData.etudes);
      } else {
        setEtudesListData(Object.values(etudesData.etudes));
      }
    } else {
      setEtudesListData([]);
    }
  }, [etudesData?.etudes]);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      if (inputSearchValue.length >= 3 || inputSearchValue.length === 0) {
        setSearchQuery(inputSearchValue);
        setCurrentPage(1);
      }
    }, 400);

    return () => clearTimeout(handler);
  }, [inputSearchValue]);

  useEffect(() => {
    setIsEtudeListDataLoading(true);
    refetchEtudeListData().finally(() => setIsEtudeListDataLoading(false));
  }, [searchQuery, refetchEtudeListData]);

  const cancelSearch = () => {
    setInputSearchValue("");
    setSearchQuery("");

    const etudesArray = etudesData?.etudes
      ? Array.isArray(etudesData.etudes)
        ? etudesData.etudes
        : Object.values(etudesData.etudes)
      : [];

    setEtudesListData(etudesArray);
  };

  const onSendClick = () => {
    openPopup(
      <MailPopup
        etudeIds={JSON.stringify(selectedRowIDs)}
        showPrice={false}
        isEtudeDetail={false}
        culture={language!}
        networkId={network?.id}
      />
    );
  };

  const onDownloadClick = useCallback(() => {
    openPopup(
      <PdfDownloadPopup
        network={network}
        selectedRowIDs={selectedRowIDs}
        isPdfURLLoading={isPdfURLLoading}
        setIsPdfURLLoading={setIsPdfURLLoading}
        isEtudeDetail={false}
      />
    );
  }, [network, isPdfURLLoading, setIsPdfURLLoading, selectedRowIDs]);

  return (
    <Page title={title}>
      {(isEtudeListDataLoading || isPdfURLLoading || isSalesNetworkLoading) && (
        <Loader />
      )}
      <group data-align="center" data-direction="column" data-min-height="fit">
        <group data-space-vertical="30" data-gap="20" data-max-length="1440">
          {/* Toolbar for selected rows */}
          {selectedRowsCount >= 2 && (
            <group
              data-length={
                currentSelectedRowSteps.current.every(
                  (step) => step === EtudeStep.Documents
                )
                  ? "autofit"
                  : 400
              }
              data-align="center"
              data-space-left="10"
              data-gap="10"
              data-border=""
              data-radius="7"
              data-name="custom_toolbar"
            >
              <text data-text-size="14" data-weight="600">
                {`${selectedRowsCount} ${t("FO_STUDIES_SELECTED_STUDIES")}`}
              </text>
              <group
                data-length="autofit"
                data-gap="10"
                data-justify="end"
                data-space-horizontal="5"
              >
                {currentSelectedRowSteps.current.every(
                  (step) => step === EtudeStep.Documents
                ) && (
                  <>
                    <Button
                      primary
                      large
                      data-length="160"
                      icon="mail"
                      text={t("FO_STUDIES_SEND")}
                      onClick={onSendClick}
                      data-text-size="15"
                      textFirst={false}
                    />
                    <Button
                      primary
                      large
                      data-length="160"
                      icon="save"
                      text={t("upload")}
                      onClick={onDownloadClick}
                      data-text-size="15"
                      textFirst={false}
                    />
                  </>
                )}
                <Button
                  data-background="error-color"
                  data-color="white"
                  large
                  data-length="160"
                  icon="delete"
                  text={t("FO_SIM_DELETE")}
                  data-text-size="15"
                  textFirst={false}
                  onClick={() => {
                    openPopup(
                      <DeletePopup
                        closePopup={closeLastPopup}
                        network={network}
                        selectedRowIDs={selectedRowIDs}
                        etudesListData={etudesListData}
                        setEtudesListDataCallback={setEtudesListData}
                        refetchEtudeListData={refetchEtudeListData}
                      />
                    );
                    setSelectedRowIDs([]);
                    setSelectedRowsCount(0);
                  }}
                />
              </group>
            </group>
          )}

          {/* Search input */}
          <group data-length="500" data-position="right" data-justify="end">
            <div className="field" data-length="480">
              <div className="form_fields">
                <div className="field_cont">
                  <input
                    type="text"
                    data-length="auto"
                    value={inputSearchValue}
                    placeholder={searchPlaceholder}
                    onChange={(e: any) => setInputSearchValue(e.target.value)}
                  />
                  <separator data-vertical="" data-margin="10"></separator>
                  <Button
                    className="button"
                    data-position="right"
                    {...(searchQuery ? { onClick: cancelSearch } : {})}
                  >
                    <icon data-margin="input">
                      {searchQuery ? "Clear" : "Search"}
                    </icon>
                  </Button>
                  <i></i>
                </div>
              </div>
            </div>
          </group>

          {/* Conditional rendering */}
          {searchQuery && etudesListData.length === 0 ? (
            // CASE 1: Search + No results
            <group
              data-border=""
              data-direction="column"
              data-justify="center"
              data-align="center"
              data-height="350"
            >
              <icon data-icon-size="48">help</icon>
              <text>{`${t("FO_STUDIES_NO_RESULTS")} ${searchQuery}`}</text>
            </group>
          ) : !searchQuery && etudesListData.length === 0 ? (
            // CASE 2: No search + No etudes
            <group
              data-border=""
              data-direction="column"
              data-justify="center"
              data-gap="20"
              data-align="center"
              data-height="350"
            >
              <group
                data-justify="center"
                data-align="center"
                data-direction="column"
              >
                <icon data-icon-size="48">help</icon>
                <text>{t("FO_STUDIES_NO_STUDIES_REGISTERED")}</text>
                <text data-text-size="10">
                  {t("FO_STUDIES_START_NEW_STUDY")}
                </text>
              </group>
              <group
                data-gap="20"
                data-name="main-thumbnail"
                data-justify="center"
              >
                {isSalesNetworkLoading && <Loader />}
                {network?.services.map((service) => (
                  <ServiceSelectItem
                    key={service.code}
                    service={service}
                    height="160"
                    length="350"
                  />
                ))}
              </group>
            </group>
          ) : (
            // CASE 3: Show table
            <MyEtudesTable
              etudesListData={etudesListData}
              setSelectedRowsCount={setSelectedRowsCount}
              setEtudesListData={setEtudesListData}
              network={network}
              setSelectedRowIDs={setSelectedRowIDs}
              currentSelectedRowSteps={currentSelectedRowSteps}
              refetchEtudeListData={refetchEtudeListData}
              etudesTotal={etudesData?.total}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              pageSize={30}
            />
          )}
        </group>
      </group>
    </Page>
  );
};
